// src/redux/company/company.profile.js

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import CrudApi from '@src/API/CrudApi';
import { getRandomKey, handleResponseErrors } from '@helpers/Actions';
import { domNotify } from '@src/@core/components/Notify/DomNotify';

const normalizeCompanyProfile = (item = {}) => {
    if (!item || typeof item !== 'object') return {};
    const { tenant, relations } = item;
    if (tenant || relations) {
        return { ...(tenant || {}), ...(relations || {}) };
    }
    return { ...item };
};

const attachRefreshKey = (company = {}, refresh = true) => ({
    ...company,
    refreshedKey: refresh ? getRandomKey() : company?.refreshedKey
});

export const getCompanyProfile = createAsyncThunk(
    'company/getProfile',
    async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    const response = await CrudApi({ useFormData: false, useFilteredBranches: false })
        .get('/api/company/profile')
        .then(res => res)
        .catch(e => handleResponseErrors(e));


    if (response?.status === 200) {
        const item = response?.data?.data?.item;
        const normalized = normalizeCompanyProfile(item);
        const currentCompany = getState()?.company?.company;
        const skipRefreshKey =
            currentCompany?.logo === normalized?.logo &&
            currentCompany?.company_stamp === normalized?.company_stamp;
        return fulfillWithValue({ ...response?.data?.data, skipRefreshKey });
    }
    return rejectWithValue(response?.data);
    }
);

export const updateCompanyProfile = createAsyncThunk(
    'company/updateProfile',
    async (payload, { fulfillWithValue, rejectWithValue }) => {
        // No refetch here: FormActions invalidates COMPANY_PROFILE_QUERY_KEY on
        // success, and that query is `getCompanyProfile` itself.
        const response = await CrudApi()
        .post('/api/company/profile/update', { ...payload })
        .then(res => ({
            status: res.status,
            response: res?.data?.data,
            payload
        }))
        .catch(e => handleResponseErrors(e));
        return response?.status === 200 ? fulfillWithValue(response?.payload): rejectWithValue(response);
    }
);

export const companySlice = createSlice({
    name: 'company',
    initialState: {
        company: {},
        isLogin: false
    },
    reducers: {
        updateCompany: (state, action) => {
            const data = action.payload || {};
            state.company = data;
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(getCompanyProfile.fulfilled, (state, action) => {
            const normalized = normalizeCompanyProfile(action?.payload?.item);
            state.company = attachRefreshKey(
                { ...normalized, refreshedKey: state.company?.refreshedKey },
                !action?.payload?.skipRefreshKey
            );
            state.isLogin = true;
        })
        .addCase(updateCompanyProfile.fulfilled, () => {
            domNotify({
                type: "success",
                title: "Updated Successfully",
                text: "Your Company Account Updated Successfully !",
            });
        })
        .addCase(updateCompanyProfile.rejected, () => {
            domNotify({
                type: "error",
                title: "Error Update",
                text: "Profile Can Not Updated.",
            });
        });
    }
});

export const { updateCompany } = companySlice.actions;

export default companySlice.reducer;
